/**
 * Playground.
 *
 * The exit criterion for this phase is that a new customer produces their first screenshot without
 * reading any documentation, so the page is built to be tried rather than studied: sensible
 * defaults, one obvious button, and the generated code appearing only after something has worked.
 *
 * It calls the **real gateway**, not a dashboard endpoint that proxies it. That is deliberate — the
 * customer watches the same request they are about to copy, with the same authentication, the same
 * limits, and the same cache behaviour. A proxy would make the playground succeed in ways their own
 * code would not.
 *
 * The API key is chosen from the customer's existing keys, and its plaintext is not available here:
 * keys are shown once at creation. So the page asks them to paste it, and keeps it in component
 * state only — never in `localStorage`, where it would outlive the tab and end up in a backup.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  PLAYGROUND_DEFAULTS,
  SNIPPET_LANGUAGES,
  buildRequestUrl,
  generateSnippet,
  type PlaygroundOptions,
  type SnippetLanguage,
} from '../../lib/playground-request';
import { useApiKeys, useWorkspace } from '../../lib/api';
import { DocsLink } from '../../components/docs-link';
import { Button, Card, Field, TextInput } from '../../components/ui';

/**
 * Where the browser sends the screenshot request.
 *
 * Configurable so the built dashboard can point at the production API. The gateway answers CORS
 * for this dashboard's origin only.
 */
const GATEWAY_ORIGIN = import.meta.env.VITE_GATEWAY_ORIGIN ?? 'http://127.0.0.1:8787';

interface Outcome {
  requestId: string | null;
  cacheStatus: string | null;
  creditsUsed: string | null;
  contentType: string | null;
  durationMs: number;
  byteLength: number;
}

interface Failure {
  code: string;
  message: string;
  requestId: string | null;
}

export function PlaygroundRoute() {
  const { data: keys } = useApiKeys();
  const { data: workspace } = useWorkspace();

  const [options, setOptions] = useState<PlaygroundOptions>(PLAYGROUND_DEFAULTS);
  const [apiKey, setApiKey] = useState('');
  const [language, setLanguage] = useState<SnippetLanguage>('curl');

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [pending, setPending] = useState(false);

  /**
   * The object URL currently displayed.
   *
   * Held in a ref as well as in state because cleanup must revoke the *latest* value without the
   * effect re-running on every render. An un-revoked blob URL pins its image in memory for the
   * lifetime of the tab, which for a full-page screenshot is megabytes per attempt.
   */
  const previewRef = useRef<string | null>(null);

  const replacePreview = useCallback((next: string | null) => {
    if (previewRef.current !== null) URL.revokeObjectURL(previewRef.current);
    previewRef.current = next;
    setPreviewUrl(next);
  }, []);

  useEffect(() => {
    return () => {
      if (previewRef.current !== null) URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
    };
  }, []);

  const activeKeys = (keys?.keys ?? []).filter((key) => key.status === 'active');
  const requestUrl = buildRequestUrl(GATEWAY_ORIGIN, options);

  function update(patch: Partial<PlaygroundOptions>): void {
    setOptions((current) => ({ ...current, ...patch }));
  }

  async function run(): Promise<void> {
    setPending(true);
    setFailure(null);
    // The previous outcome goes immediately. Leaving it on screen would show one run's cache
    // status and credit cost next to the image from another, which is worse than showing nothing.
    setOutcome(null);
    const startedAt = performance.now();

    try {
      const response = await fetch(requestUrl, {
        headers: { authorization: `Bearer ${apiKey.trim()}` },
      });

      const requestId = response.headers.get('x-request-id');

      if (!response.ok) {
        // The documented error envelope. Readable here only because the gateway exposes the
        // headers and answers CORS on errors as well as successes.
        const body = (await response.json().catch(() => null)) as {
          error?: { code?: string; message?: string };
        } | null;

        replacePreview(null);
        setOutcome(null);
        setFailure({
          code: body?.error?.code ?? 'request_failed',
          message: body?.error?.message ?? 'The screenshot request failed.',
          requestId,
        });
        return;
      }

      const blob = await response.blob();
      replacePreview(URL.createObjectURL(blob));
      setOutcome({
        requestId,
        cacheStatus: response.headers.get('x-urlshot-cache'),
        creditsUsed: response.headers.get('x-urlshot-credits-used'),
        contentType: response.headers.get('content-type'),
        durationMs: Math.round(performance.now() - startedAt),
        byteLength: blob.size,
      });
    } catch {
      // A network-level failure, which in a browser also covers a blocked cross-origin request.
      replacePreview(null);
      setOutcome(null);
      setFailure({
        code: 'unreachable',
        message: `The API at ${GATEWAY_ORIGIN} could not be reached.`,
        requestId: null,
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <div>
        <h1 className="text-xl font-semibold">Playground</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Build a request, run it against the real API, and copy the code that reproduces it. Every
          option, including ones this page does not offer, is described in the{' '}
          <DocsLink section="parameters">parameter reference</DocsLink>.
        </p>
      </div>

      {activeKeys.length === 0 && (
        <Card className="border-brand-400 ring-1 ring-brand-400">
          <h2 className="text-base font-semibold">You need an API key first</h2>
          <p className="mt-1 text-sm text-neutral-600">
            Keys are shown once when created, so paste yours below after making one.
          </p>
          <Link to="/api-keys" className="mt-4 inline-block">
            <Button data-testid="playground-create-key">Create an API key</Button>
          </Link>
        </Card>
      )}

      <Card>
        <Field
          label="API key"
          htmlFor="playground-key"
          hint="Kept in this page only. It is never stored or sent anywhere else."
        >
          <TextInput
            data-testid="playground-api-key"
            id="playground-key"
            type="password"
            autoComplete="off"
            placeholder="sk_..."
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
          />
        </Field>
      </Card>

      <Card>
        <h2 className="text-base font-semibold">Request</h2>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="URL" htmlFor="playground-url-input">
            <TextInput
              data-testid="playground-url"
              id="playground-url-input"
              value={options.url}
              onChange={(event) => update({ url: event.target.value })}
            />
          </Field>

          <Field label="Format" htmlFor="playground-format-select">
            <select
              data-testid="playground-format"
              id="playground-format-select"
              className="w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm"
              value={options.format}
              onChange={(event) =>
                update({ format: event.target.value as PlaygroundOptions['format'] })
              }
            >
              <option value="png">PNG</option>
              <option value="jpeg">JPEG</option>
              <option value="webp">WebP</option>
            </select>
          </Field>

          <Field label="Viewport width" htmlFor="playground-vw">
            <TextInput
              data-testid="playground-viewport-width"
              id="playground-vw"
              type="number"
              value={String(options.viewportWidth)}
              onChange={(event) => update({ viewportWidth: Number(event.target.value) })}
            />
          </Field>

          <Field label="Viewport height" htmlFor="playground-vh">
            <TextInput
              data-testid="playground-viewport-height"
              id="playground-vh"
              type="number"
              value={String(options.viewportHeight)}
              onChange={(event) => update({ viewportHeight: Number(event.target.value) })}
            />
          </Field>

          <Field label="Device scale factor" htmlFor="playground-dsf">
            <TextInput
              id="playground-dsf"
              type="number"
              value={String(options.deviceScaleFactor)}
              onChange={(event) => update({ deviceScaleFactor: Number(event.target.value) })}
            />
          </Field>

          {options.format !== 'png' && (
            <Field label="Quality" htmlFor="playground-quality-input" hint="Lossy formats only.">
              <TextInput
                data-testid="playground-quality"
                id="playground-quality-input"
                type="number"
                value={String(options.quality ?? 80)}
                onChange={(event) => update({ quality: Number(event.target.value) })}
              />
            </Field>
          )}

          <Field label="Wait until" htmlFor="playground-wait">
            <select
              id="playground-wait"
              className="w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm"
              value={options.waitUntil}
              onChange={(event) =>
                update({ waitUntil: event.target.value as PlaygroundOptions['waitUntil'] })
              }
            >
              <option value="auto">auto</option>
              <option value="load">load</option>
              <option value="domcontentloaded">domcontentloaded</option>
              <option value="networkidle0">networkidle0</option>
              <option value="networkidle2">networkidle2</option>
            </select>
          </Field>

          <Field
            label="Delay (ms)"
            htmlFor="playground-delay"
            hint={`Plan maximum ${String(workspace?.limits.maxDelayMs ?? 0)}.`}
          >
            <TextInput
              id="playground-delay"
              type="number"
              value={String(options.delayMs)}
              onChange={(event) => update({ delayMs: Number(event.target.value) })}
            />
          </Field>

          <Field
            label="Timeout (ms)"
            htmlFor="playground-timeout"
            hint={`Plan maximum ${String(workspace?.limits.maxTimeoutMs ?? 0)}.`}
          >
            <TextInput
              id="playground-timeout"
              type="number"
              value={String(options.timeoutMs)}
              onChange={(event) => update({ timeoutMs: Number(event.target.value) })}
            />
          </Field>

          <Field
            label="Cache TTL (seconds)"
            htmlFor="playground-cache"
            hint="0 renders fresh every time. Above 0, an identical request is served from cache and costs no credit."
          >
            <TextInput
              data-testid="playground-cache-ttl"
              id="playground-cache"
              type="number"
              value={String(options.cacheTtl)}
              onChange={(event) => update({ cacheTtl: Number(event.target.value) })}
            />
          </Field>

          <Field
            label="Hide selectors"
            htmlFor="playground-hide"
            hint="Comma separated. Applied after the page loads."
          >
            <TextInput
              data-testid="playground-hide-selectors"
              id="playground-hide"
              placeholder="#banner, .cookie-bar"
              value={options.hideSelectors.join(', ')}
              onChange={(event) =>
                update({ hideSelectors: event.target.value.split(',').map((part) => part.trim()) })
              }
            />
          </Field>

          <Field
            label="Capture one element"
            htmlFor="playground-selector"
            hint="A CSS selector. The first match is captured instead of the viewport."
          >
            <TextInput
              data-testid="playground-selector"
              id="playground-selector"
              placeholder="#pricing"
              value={options.selector}
              onChange={(event) => update({ selector: event.target.value })}
            />
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap gap-4">
          {/* Hidden while a selector is entered: the two cannot be combined, and an element is
              captured whole however tall it is. */}
          {options.selector.trim() === '' && (
            <label className="flex items-center gap-2 text-sm">
              <input
                data-testid="playground-full-page"
                type="checkbox"
                checked={options.fullPage}
                onChange={(event) => update({ fullPage: event.target.checked })}
              />
              Full page
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              data-testid="playground-dark-mode"
              type="checkbox"
              checked={options.darkMode}
              onChange={(event) => update({ darkMode: event.target.checked })}
            />
            Dark mode
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              data-testid="playground-block-cookie-banners"
              type="checkbox"
              checked={options.blockCookieBanners}
              onChange={(event) => update({ blockCookieBanners: event.target.checked })}
            />
            Block cookie banners
          </label>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <Button
            data-testid="playground-run"
            disabled={pending || apiKey.trim() === '' || options.url.trim() === ''}
            onClick={() => void run()}
          >
            {pending ? 'Rendering…' : 'Take screenshot'}
          </Button>
          {pending && (
            <span className="text-xs text-neutral-500">This can take a few seconds.</span>
          )}
        </div>
      </Card>

      {failure !== null && (
        <Card className="border-red-200 bg-red-50" data-testid="playground-error">
          <h2 className="text-base font-semibold text-red-900">{failure.code}</h2>
          <p className="mt-1 text-sm text-red-900">{failure.message}</p>
          {failure.requestId !== null && (
            <p className="mt-2 text-xs text-red-800">Request ID: {failure.requestId}</p>
          )}
          <p className="mt-2 text-xs text-red-800">
            What each code means, and whether retrying helps, is in the{' '}
            <DocsLink section="errors" className="link font-medium">
              error reference
            </DocsLink>
            .
          </p>
        </Card>
      )}

      {previewUrl !== null && outcome !== null && (
        <Card data-testid="playground-result">
          <h2 className="text-base font-semibold">Result</h2>

          <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-xs uppercase tracking-wide text-neutral-500">Cache</dt>
              <dd className="mt-1 font-medium" data-testid="playground-cache-status">
                {outcome.cacheStatus ?? '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-neutral-500">Credits used</dt>
              <dd className="mt-1 font-medium" data-testid="playground-credits">
                {outcome.creditsUsed ?? '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-neutral-500">Duration</dt>
              <dd className="mt-1 font-medium">{outcome.durationMs} ms</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-neutral-500">Content type</dt>
              <dd className="mt-1 font-medium">{outcome.contentType ?? '—'}</dd>
            </div>
          </dl>

          <p className="mt-2 text-xs text-neutral-500" data-testid="playground-request-id">
            Request ID: {outcome.requestId ?? '—'} · {Math.round(outcome.byteLength / 1024)} KB
          </p>

          <img
            data-testid="playground-preview"
            src={previewUrl}
            alt="Screenshot preview"
            className="mt-4 w-full rounded border border-neutral-200"
          />
        </Card>
      )}

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-semibold">Code</h2>
          <div className="flex gap-2">
            {SNIPPET_LANGUAGES.map((option) => (
              <Button
                key={option.id}
                variant={option.id === language ? 'primary' : 'secondary'}
                data-testid={`playground-language-${option.id}`}
                onClick={() => setLanguage(option.id)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        <p className="mt-2 text-xs text-neutral-500">
          Reads the key from a <code>URLSHOT_API_KEY</code> environment variable rather than
          embedding it, so the snippet is safe to paste into a repository.
        </p>

        <pre
          className="mt-3 overflow-x-auto rounded bg-neutral-50 p-3 text-xs"
          data-testid="playground-snippet"
        >
          {generateSnippet(language, GATEWAY_ORIGIN, options)}
        </pre>
      </Card>
    </>
  );
}
